import React from 'react';
// @ts-ignore - Volto core export, no .d.ts
import { ImageInput } from '@plone/volto/components/manage/Widgets/ImageWidget';
// @ts-ignore
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import './BlockImageInput.scss';

export interface ObjectBrowserImageItem {
  '@id': string;
  title?: string;
  image_field?: string;
  image_scales?: any;
  [key: string]: any;
}

export type BlockImageValue = ObjectBrowserImageItem[] | undefined;

// Layout of the empty state. "default" shows the full Volto placeholder.
// "strip" is a slim bar with a label and the buttons. "tile" shows only the
// buttons and fits in a small square.
export type BlockImageInputVariant = 'default' | 'strip' | 'tile';

interface BlockImageInputProps {
  id: string;
  block: string;
  value: BlockImageValue;
  onChange: (next: BlockImageValue) => void;
  imageSize?: string;
  placeholderLinkInput?: string;
  // Label of the empty state. Volto shows its own text when it is not set.
  description?: string;
  hideLinkPicker?: boolean;
  variant?: BlockImageInputVariant;
  children?: React.ReactNode;
  className?: string;
}

const isFilled = (value: BlockImageValue): value is ObjectBrowserImageItem[] =>
  Array.isArray(value) && value.length > 0 && Boolean(value[0]?.['@id']);

const BlockImageInput = ({
  id,
  block,
  value,
  onChange,
  imageSize = 'large',
  placeholderLinkInput,
  description,
  hideLinkPicker = false,
  variant = 'default',
  children,
  className,
}: BlockImageInputProps) => {
  const subrequestKey = `${block}-${id}`;

  const normalize = React.useCallback(
    (_fieldId: string, payload: any, meta?: any): BlockImageValue => {
      if (payload === null || payload === undefined || payload === '') {
        return undefined;
      }
      if (Array.isArray(payload)) {
        return payload as ObjectBrowserImageItem[];
      }
      if (typeof payload === 'string') {
        const item: ObjectBrowserImageItem = {
          '@id': flattenToAppURL(payload),
        };
        if (meta?.title) item.title = meta.title;
        if (meta?.image_field) item.image_field = meta.image_field;
        if (meta?.image_scales) item.image_scales = meta.image_scales;
        return [item];
      }
      if (typeof payload === 'object') {
        const raw = payload['@id'] ?? '';
        const item: ObjectBrowserImageItem = {
          ...payload,
          '@id': raw ? flattenToAppURL(raw) : raw,
        };
        return [item];
      }
      return undefined;
    },
    [],
  );

  const handleInputChange = React.useCallback(
    (fieldId: string, payload: any, meta?: any) => {
      onChange(normalize(fieldId, payload, meta));
    },
    [normalize, onChange],
  );

  const handleSelectItem = React.useCallback(
    (url: string, item: any) => {
      onChange([
        {
          '@id': flattenToAppURL(url),
          title: item?.title,
          image_field: item?.image_field,
          image_scales: item?.image_scales,
        },
      ]);
    },
    [onChange],
  );

  if (isFilled(value)) {
    return (
      <div
        className={['block-image-input', 'block-image-input--set', className]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
        <button
          type="button"
          className="block-image-input__clear"
          aria-label="Retirer l'image"
          title="Retirer l'image"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChange(undefined);
          }}
        >
          ×
        </button>
      </div>
    );
  }

  return (
    <div
      className={[
        'block-image-input',
        'block-image-input--empty',
        variant !== 'default' && `block-image-input--${variant}`,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Volto reads an empty list as an image URL and crashes. */}
      <ImageInput
        id={id}
        block={subrequestKey}
        value={undefined}
        onChange={handleInputChange}
        onSelectItem={handleSelectItem}
        objectBrowserPickerType="image"
        imageSize={imageSize}
        placeholderLinkInput={placeholderLinkInput}
        description={description}
        hideLinkPicker={hideLinkPicker}
      />
    </div>
  );
};

export default BlockImageInput;
